// LeadConvertDialog — диалог конверсии лида в сделку (V12 U-34-B, Tech-Story §3.1).
// Min-поля (Tech-Story §3.7): title (default = lead.title), amount/currency,
// departmentId + ownerUserId, dueDate → POST /api/leads/{id}/convert.
// Успех → onConverted(deal) — родитель навигирует к сделке и инвалидирует ['leads'];
// ошибка (в т.ч. 409 DEAL-ALREADY-CONVERTED) → alert + refetch лида.

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { DatePickerField } from '@/components/forms';
import { DepartmentAutocomplete } from '@/components/DepartmentAutocomplete';
import { UserAutocomplete } from '@/components/UserAutocomplete';
import { leadsApi, type Lead } from '@/api/leads';
import type { Deal } from '@/api/deals';
import { useAuth } from '@/auth/AuthContext';
import { extractApiErrorMessage } from '@/lib/apiError';
import { parseAmount } from '@/lib/money';
import { t } from '@/i18n';

const CURRENCIES = ['RUB', 'USD', 'EUR', 'CNY'] as const;

/** true если дата (yyyy-MM-dd) не задана или сегодня/позже (канон DealNewPage U-17). */
function isTodayOrFuture(v: string | undefined): boolean {
  return !v || new Date(v).getTime() >= new Date(new Date().toDateString()).getTime();
}

const convertSchema = z.object({
  title: z.string().min(1, 'Заголовок обязателен').max(255, 'Слишком длинный заголовок'),
  // Сумма опциональна (пусто → backend ставит 0, Q17-канон ≥0); отрицательная — ошибка.
  amount: z
    .string()
    .refine((v) => v.trim() === '' || parseAmount(v) !== null, { message: 'Укажите сумму числом' })
    .refine((v) => (parseAmount(v) ?? 0) >= 0, { message: 'Сумма должна быть ≥ 0' }),
  currency: z.enum(CURRENCIES),
  departmentId: z.string().min(1, 'Выберите департамент'),
  ownerUserId: z.string().min(1, 'Владелец обязателен'),
  dueDate: z.string().optional().or(z.literal('')).refine(isTodayOrFuture, 'Дата не может быть в прошлом'),
});

type ConvertFormValues = z.infer<typeof convertSchema>;

interface LeadConvertDialogProps {
  open: boolean;
  /** Лид для конверсии (null — диалог не открыт). */
  lead: Lead | null;
  onClose: () => void;
  /** Успех конверсии: родитель навигирует к сделке в scope + invalidate лиды. */
  onConverted: (deal: Deal) => void;
}

/** Диалог конверсии лида в сделку (минимум полей; дефолты — из лида/текущего пользователя). */
export function LeadConvertDialog({ open, lead, onClose, onConverted }: LeadConvertDialogProps): JSX.Element {
  const queryClient = useQueryClient();
  const { currentUser } = useAuth();

  const { control, handleSubmit } = useForm<ConvertFormValues>({
    resolver: zodResolver(convertSchema),
    defaultValues: {
      title: lead?.title ?? '',
      amount: '',
      currency: 'RUB',
      departmentId: currentUser?.departments[0]?.id ?? '',
      ownerUserId: currentUser?.id ?? '',
      dueDate: '',
    },
  });

  const mutation = useMutation({
    mutationFn: (values: ConvertFormValues) =>
      leadsApi.convert(lead?.id ?? '', {
        title: values.title,
        ...(parseAmount(values.amount) !== null ? { amount: parseAmount(values.amount) } : {}),
        currency: values.currency,
        departmentId: values.departmentId,
        ownerUserId: values.ownerUserId,
        dueDate: values.dueDate || null,
      }),
    onSuccess: (deal) => {
      onConverted(deal);
      onClose();
    },
    onError: () => {
      // 409 или иное: refetch лида — свежий статус/version (канон B2).
      void queryClient.invalidateQueries({ queryKey: ['lead', lead?.id] });
    },
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t('leads.convert.dialogTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {mutation.isError ? (
            <Alert severity="error">{extractApiErrorMessage(mutation.error, t('leads.convert.error'))}</Alert>
          ) : null}
          <Controller
            name="title"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Заголовок сделки"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Заголовок сделки' }}
              />
            )}
          />
          <Controller
            name="amount"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Сумма"
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                inputProps={{ 'aria-label': 'Сумма' }}
              />
            )}
          />
          <Controller
            name="currency"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Валюта"
                select
                fullWidth
                inputProps={{ 'aria-label': 'Валюта' }}
              >
                {CURRENCIES.map((currency) => (
                  <MenuItem key={currency} value={currency}>
                    {currency}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="departmentId"
            control={control}
            render={({ field, fieldState }) => (
              <DepartmentAutocomplete
                label={t('crm.deals.department')}
                value={field.value}
                onChange={field.onChange}
                required
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                aria-label={t('crm.deals.department')}
              />
            )}
          />
          <Controller
            name="ownerUserId"
            control={control}
            render={({ field, fieldState }) => (
              <UserAutocomplete
                label="Владелец"
                value={field.value}
                onChange={field.onChange}
                required
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
                aria-label="Владелец"
              />
            )}
          />
          <DatePickerField
            name="dueDate"
            control={control}
            label="Дата закрытия"
            aria-label="Дата закрытия"
            minDate="today"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => handleSubmit((values) => mutation.mutate(values))()}
          disabled={mutation.isPending}
        >
          {t('leads.convert.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
